import { useCommonFormsTranslation } from '@libs/utils/translations'
import { isValidTime, hoursRegex } from '@spa/components/FormFields/helpers'
import { sumBy } from 'lodash-es'
import moment from 'moment'
import * as yup from 'yup'

import isValidTimeRange from './isValidTimeRange'

yup.addMethod(yup.array, 'unique', function (message, mapper = a => a) {
  return this.test(
    'unique',
    message,
    list => list.length === new Set(list.map(mapper)).size
  )
})

const emptyStringToZero = (value, originalValue) => {
  if (typeof originalValue === 'string' && originalValue === '') {
    return 0
  }
  return value
}

export const useBreakduration = () => {
  const { t } = useCommonFormsTranslation()

  return yup
    .number()
    .transform(value => (Number.isNaN(value) ? 0 : value))
    .moreThan(-1, t('commonForms.forms.shift.breakTimeDuration'))
    .lessThan(121, t('commonForms.forms.shift.breakTimeDuration'))
}

export const useShiftTimeRange = () => {
  const { t } = useCommonFormsTranslation()

  return yup
    .array()
    .of(
      yup
        .string()
        .matches(hoursRegex, t('commonForms.errors.hoursDigitFormat'))
        .required()
    )
    .test('valid_hours', t('commonForms.errors.hoursDigitFormat'), hours => {
      const validTimeSlot = hours.some(slot => slot.includes('_'))

      return !validTimeSlot
    })
    .unique(t('commonForms.errors.notEqual'))
}

export const useMealType = () => {
  const { t } = useCommonFormsTranslation()

  return yup.mixed().when('meal', {
    is: meal => meal,
    then: yup.mixed().required(t('commonForms.forms.shift.meal'))
  })
}

export const useBreaks = () => {
  const { t } = useCommonFormsTranslation()

  return (
    yup
      .array()
      .of(
        yup.object({
          value: yup
            .object()
            .shape({
              planned_duration: yup
                .number(t('commonForms.errors.validNumber'))
                .min(0, t('commonForms.forms.shift.breakTimeHigherThan0'))
                .transform(emptyStringToZero)
                .nullable()
            })

            /**
             * Check that each break timerange is contained in the shift timerange
             */
            .test(
              'shift_range_contains_break',
              t('commonForms.forms.shift.breakTimeInShift'),
              function (item) {
                const [shiftStart, shiftEnd] =
                  this.from[2].value.shift_time_range
                const [breakStart, breakEnd] = item.timeRange || []

                return isValidTimeRange(
                  shiftStart,
                  shiftEnd,
                  breakStart,
                  breakEnd
                )
              }
            )

            /**
             * Compare each break with previous ones to check if timeRanges are overlapping
             */
            .test(
              'overlapping_break',
              t('commonForms.forms.shift.breakTimeAlreadyExisted'),
              function (item) {
                const index = parseInt(
                  this.path.split('[')[1].split(']')[0],
                  10
                )
                const breaksValues = this.from[2].value.breaks.map(i => i.value)

                const overlaps = breaksValues
                  .filter(
                    (breakItem, idx) =>
                      index > idx &&
                      // @ts-ignore migration from js(x) to ts(x)
                      isValidTime({ time: breakItem.timeRange?.[0] || '' }) &&
                      // @ts-ignore migration from js(x) to ts(x)
                      isValidTime({ time: breakItem.timeRange?.[1] || '' })
                  )
                  .filter(i => {
                    const currentRange = moment.range(
                      moment(item.timeRange[0], 'HH:mm'),
                      moment(item.timeRange[1], 'HH:mm')
                    )
                    const compareRange = moment.range(
                      moment(i.timeRange[0], 'HH:mm'),
                      moment(i.timeRange[1], 'HH:mm')
                    )
                    return currentRange.overlaps(compareRange)
                  })

                return overlaps.length === 0
              }
            )
        })
      )

      /**
       * Total sum of all breaks duration should not exceed the shift duration
       */
      .test(
        'breaks_duration_exceed',
        t('commonForms.forms.shift.breakTimeExceededShift'),
        function (breaksValues) {
          if (!breaksValues) {
            return true
          }

          const breaks = breaksValues.map(i => i.value)
          const [shiftStart, shiftEnd] = this.parent.shift_time_range
          const momentStart = moment(shiftStart, 'HH:mm')
          const momentEnd = moment(shiftEnd, 'HH:mm')

          if (momentEnd.isBefore(momentStart)) {
            momentEnd.add(1, 'day')
          }

          const shiftDurationMin = moment
            .duration(momentEnd.diff(momentStart))
            .asMinutes()

          // @ts-ignore migration from js(x) to ts(x)
          const breaksSum = sumBy(breaks, i => Number(i.plannedDuration || 0))
          return breaksSum < shiftDurationMin
        }
      )
  )
}

export const meal = yup.boolean()
